import type { CustomFieldType } from './custom-field';

/**
 * What makes a rendered custom field unique on screen, which is what its test id is built from.
 *
 * - A legacy field (the warehouse template) is unique by type and label: the template validator
 *   rejects a repeated pair. Its `name` is not used, because it is set once and goes stale when
 *   the field's type is edited.
 * - A Custom Forms field is unique by its form field and the trigger it is shown for: the same
 *   flow can be attached to more than one trigger, rendering the same form field more than once.
 */
export type CustomFieldTestIdentity =
  | { type: CustomFieldType; label: string }
  | { formFieldId: string; triggerId: string };

/** Every ASCII punctuation character, spelled out so a label's id keeps it without breaking CSS. */
const PUNCTUATION_NAMES: Readonly<Record<string, string>> = {
  '!': 'exclamation',
  '"': 'quote',
  '#': 'hash',
  $: 'dollar',
  '%': 'percent',
  '&': 'amp',
  "'": 'apostrophe',
  '(': 'lparen',
  ')': 'rparen',
  '*': 'asterisk',
  '+': 'plus',
  ',': 'comma',
  '-': 'dash',
  '.': 'dot',
  '/': 'slash',
  ':': 'colon',
  ';': 'semicolon',
  '<': 'lt',
  '=': 'equals',
  '>': 'gt',
  '?': 'question',
  '@': 'at',
  '[': 'lbracket',
  '\\': 'backslash',
  ']': 'rbracket',
  '^': 'caret',
  _: 'underscore',
  '`': 'backtick',
  '{': 'lbrace',
  '|': 'pipe',
  '}': 'rbrace',
  '~': 'tilde'
};

const ASCII_PUNCTUATION = /[!-/:-@[-`{-~]/gu;

/**
 * A label as a slug that is safe in a CSS selector, which the date picker's popup lookup needs:
 * lowercased, each run of whitespace a dash, and each punctuation character spelled out, so
 * `PO #` becomes `po-hash` and `Dock-Number` (`dock-dash-number`) stays apart from `Dock Number`
 * (`dock-number`). Anything else, such as accented letters, is kept as is.
 *
 * Two labels can still share a slug, which is accepted because no one would write such a pair:
 * labels that differ only in repeated whitespace, and a label spelling out a symbol's name
 * (`PO hash` and `PO #`).
 */
function toTestIdSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(ASCII_PUNCTUATION, character => `-${PUNCTUATION_NAMES[character]}-`)
    .replace(/\s+/gu, '-')
    .replace(/-{2,}/gu, '-')
    .replace(/^-|-$/gu, '');
}

/**
 * The `data-testid` of a custom field's control. The app and the E2E helpers both build it from
 * here, so a field is always found by the same rule. Parts of a composite field (a timestamp's
 * date and time) append a suffix to it.
 */
export function getCustomFieldTestId(identity: CustomFieldTestIdentity): string {
  if ('formFieldId' in identity) {
    return `custom-field-${identity.formFieldId}-${identity.triggerId}`;
  }

  return `custom-field-${identity.type}-${toTestIdSlug(identity.label)}`;
}

/**
 * The `data-testid` of one option inside a field that lists its choices, such as an Action
 * field's feature radios: the field's own id, then the option's value as a slug.
 */
export function getCustomFieldOptionTestId(fieldTestId: string, option: string): string {
  return `${fieldTestId}-option-${toTestIdSlug(option)}`;
}
